import { inject, Injectable } from '@angular/core';
import { 
  Storage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from '@angular/fire/storage';
import { Observable, from, of, throwError } from 'rxjs';
import { mergeMap, catchError, map, tap, switchMap } from 'rxjs/operators';
import { UserFile, FileUploadResponse } from '../models/file-upload.model';
import { FIREBASE_AUTH, FIREBASE_STORAGE } from '../../fireauth-lib.config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { 
  UserFilesService as MongoUserFilesService, 
  CreateUserFileDto,
  UserFilePopulated,
  FileListResponse
} from 'cms-post-lib';
import { FileStreamService } from './file-stream.service';
import {
  StreamOptions,
  FileStreamResult,
  ConversionOptions,
  ConversionResult,
  AccessTokenResponse
} from '../models/file-streaming.model';


@Injectable({
  providedIn: 'root'
})
export class UserFilesService {
  private readonly storage = inject(FIREBASE_STORAGE);
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly http = inject(HttpClient);
  private readonly mongoFilesService = inject(MongoUserFilesService);
  private readonly fileStreamService = inject(FileStreamService);
  
  constructor() {}
  
  /**
   * Upload a file to Firebase Storage and save metadata to MongoDB
   * 
   * @param file - File to upload
   * @param userId - User ID (extUserId from Firebase Auth)
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @returns Observable emitting upload progress (0-100) or FileUploadResponse on completion
   */
  uploadFile(file: File, userId: string, orgId: string): Observable<number | FileUploadResponse> {
    if (!userId || !orgId) {
      return throwError(() => new Error('userId and orgId are required'));
    }
    
    // Create optimized storage path to avoid conflicts
    const fileExtension = file.name.split('.').pop();
    const timestamp = new Date().getTime();
    const uniqueFileName = `${timestamp}_${this.generateRandomString(8)}.${fileExtension}`;
    const storagePath = `files/${userId}/${orgId}/${uniqueFileName}`;
    
    // Create storage reference
    const storageRef = ref(this.storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Observable<number | FileUploadResponse>(observer => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Report upload progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next(progress);
        },
        (error) => {
          // Handle upload error
          observer.error(error);
        },
        () => {
          // Upload completed successfully, now get the download URL
          getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
            // Save file metadata to MongoDB via API
            this.saveFileMetadata({
              fileName: file.name,
              orgId,
              urls: downloadURL,
              storagePath,
              fileType: file.type,
              fileSize: file.size
            }).pipe(
              catchError(error => {
                // If MongoDB save fails, delete the uploaded file
                deleteObject(storageRef);
                return throwError(() => error);
              })
            ).subscribe({
              next: () => {
                observer.next({ url: downloadURL, path: storagePath });
                observer.complete();
              },
              error: (err) => observer.error(err)
            });
          }).catch(error => observer.error(error));
        }
      );
    });
  }
  
  /**
   * Save file metadata to MongoDB via API
   * 
   * @param fileData - File creation DTO
   * @returns Observable of created file ID
   */
  private saveFileMetadata(fileData: CreateUserFileDto): Observable<string> {
    return this.mongoFilesService.createFile(fileData).pipe(
      map(response => response.data.file._id),
      catchError(error => {
        console.error('Error saving file metadata to MongoDB:', error);
        return throwError(() => new Error('Failed to save file metadata'));
      })
    );
  }
  
  /**
   * Get all files for a user and organization from MongoDB
   * 
   * @param userId - User ID (not used in current API, but kept for backward compatibility)
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @returns Observable of UserFile array (mapped from MongoDB response)
   */
  getUserFiles(userId: string, orgId: string): Observable<UserFile[]> {
    return this.mongoFilesService.getFilesByOrganization(orgId, { page: 1, limit: 100 }).pipe(
      map(response => this.mapMongoFilesToUserFiles(response)),
      catchError(error => {
        console.error('Error fetching files from MongoDB:', error);
        return of([]);
      })
    );
  }
  
  /**
   * Delete a file from Storage and its metadata from MongoDB
   * 
   * @param fileId - MongoDB file ID
   * @param storagePath - Firebase Storage path
   * @returns Observable that completes when both operations succeed
   */
  deleteFile(fileId: string, storagePath: string): Observable<void> {
    // Delete from Storage first
    const storageRef = ref(this.storage, storagePath);
    
    return from(deleteObject(storageRef)).pipe(
      switchMap(() => this.mongoFilesService.deleteFile(fileId)),
      map(() => undefined),
      catchError(error => {
        console.error('Error deleting file:', error);
        return throwError(() => new Error('Failed to delete file'));
      })
    );
  }
  
  /**
   * Map MongoDB UserFilePopulated array to local UserFile array
   * 
   * @param response - MongoDB API response
   * @returns Array of UserFile objects
   */
  private mapMongoFilesToUserFiles(response: FileListResponse): UserFile[] {
    return response.data.files.map(file => ({
      id: file._id,
      userId: typeof file.userId === 'string' ? file.userId : file.userId._id,
      orgCode: typeof file.orgId === 'string' ? file.orgId : file.orgId._id,
      fileName: file.fileName,
      fileType: file.fileType,
      url: file.urls,
      storagePath: file.storagePath,
      createdAt: new Date(file.createdAt)
    }));
  }

  /**
   * Generate a random string of specified length
   */
  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  fetchFiles(path: string, baseUrl: string): Observable<Blob> {
    baseUrl = `${baseUrl}fetchFile`
    return from(this.auth.currentUser!.getIdToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.get(`${baseUrl}?path=${encodeURIComponent(path)}`, {
          headers,
          responseType: 'blob'
        });
      })
    );
  }

  // ============================================================================
  // File Streaming Methods (delegated to FileStreamService)
  // ============================================================================

  /**
   * Stream a file securely from the API
   * 
   * @remarks
   * Convenience method that delegates to FileStreamService.
   * Streams file content with authentication and optional byte-range support.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits the file as a Blob
   * 
   * @example
   * ```typescript
   * const stream$ = await this.userFilesService.streamFile(fileId);
   * stream$.subscribe(blob => {
   *   const url = URL.createObjectURL(blob);
   *   window.open(url);
   * });
   * ```
   */
  async streamFile(fileId: string, options?: StreamOptions): Promise<Observable<Blob>> {
    return this.fileStreamService.streamFile(fileId, options);
  }

  /**
   * Stream a file with metadata
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits FileStreamResult with blob and metadata
   */
  async streamFileWithMetadata(fileId: string, options?: StreamOptions): Promise<Observable<FileStreamResult>> {
    return this.fileStreamService.streamFileWithMetadata(fileId, options);
  }

  /**
   * Stream a file with progress tracking
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits progress (0-100) and final Blob
   */
  async streamFileWithProgress(fileId: string, options?: StreamOptions): Promise<Observable<number | Blob>> {
    return this.fileStreamService.streamFileWithProgress(fileId, options);
  }

  // ============================================================================
  // Access Token Methods (delegated to FileStreamService)
  // ============================================================================

  /**
   * Generate a temporary access token for a file
   * 
   * @remarks
   * Creates a time-limited JWT token (60-3600 seconds).
   * Useful for temporary file sharing or embedding.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param expiresIn - Token expiration time in seconds (60-3600)
   * @returns Observable that emits the access token response
   * 
   * @example
   * ```typescript
   * this.userFilesService.generateAccessToken(fileId, 1800)
   *   .subscribe(response => {
   *     console.log('Token:', response.accessToken);
   *     console.log('Expires:', response.expiresAt);
   *   });
   * ```
   */
  generateAccessToken(fileId: string, expiresIn: number): Observable<AccessTokenResponse> {
    return this.fileStreamService.generateAccessToken(fileId, expiresIn);
  }

  /**
   * Generate a quick access token (5 minutes)
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits the access token
   */
  generateQuickAccessToken(fileId: string): Observable<AccessTokenResponse> {
    return this.fileStreamService.generateQuickAccessToken(fileId);
  }

  /**
   * Generate a standard access token (30 minutes)
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits the access token
   */
  generateStandardAccessToken(fileId: string): Observable<AccessTokenResponse> {
    return this.fileStreamService.generateStandardAccessToken(fileId);
  }

  /**
   * Revoke all active access tokens for a file
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits revocation result
   */
  revokeFileAccessTokens(fileId: string): Observable<any> {
    return this.fileStreamService.revokeFileAccessTokens(fileId);
  }

  // ============================================================================
  // File Conversion Methods (delegated to FileStreamService)
  // ============================================================================

  /**
   * Convert a file to a web-viewable format
   * 
   * @remarks
   * Converts office documents (DOCX, XLSX, PPTX) to HTML, PDF, or text.
   * Results are cached by default for better performance.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Conversion configuration (format, page, cache)
   * @returns Observable that emits the conversion result
   * 
   * @example
   * ```typescript
   * // Convert DOCX to HTML
   * this.userFilesService.convertFile(fileId, { format: 'html' })
   *   .subscribe(result => {
   *     this.displayHtml(result.content);
   *   });
   * 
   * // Convert Excel sheet 2
   * this.userFilesService.convertFile(fileId, { format: 'html', page: 2 })
   *   .subscribe(result => {
   *     this.sheetHtml = result.content;
   *   });
   * ```
   */
  convertFile(fileId: string, options: ConversionOptions): Observable<ConversionResult> {
    return this.fileStreamService.convertFile(fileId, options);
  }

  /**
   * Invalidate the conversion cache for a file
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that completes when cache is cleared
   */
  invalidateConversionCache(fileId: string): Observable<void> {
    return this.fileStreamService.invalidateConversionCache(fileId);
  }
}

